import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { AuditLog, AuditLogFilters } from '../../../models/auditLog';
import { fetchAuditLogs } from '../../../services/admin/auditLogService';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import { AuditFiltersPanel, type AuditCompanyOption } from '../components/AuditFiltersPanel';
import { AuditLogTable } from '../components/AuditLogTable';
import { AuditLogDetailModal } from '../components/AuditLogDetailModal';

/** Audit log rows requested per page (server-driven pagination — see auditLogService.fetchAuditLogs). */
const PAGE_SIZE = 25;

export function AdminAuditLogPage(): React.ReactElement {
  useDocumentTitle('Audit logs · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();

  const [rows, setRows] = useState<AuditLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState<AuditCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAuditLogs(getAccessToken, filters, page, PAGE_SIZE);
      setRows(result.items);
      setTotalCount(result.totalCount);
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setError(err instanceof Error ? err.message : 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters, page]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Company options for the filter picker (reuses the dashboard company list, same as discount codes).
  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const companyRows = await fetchAdminDashboard(acquireAccessToken);
        if (isMounted) {
          setCompanies(companyRows.map((c) => ({ companyId: c.companyId, companyName: c.companyName })));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [acquireAccessToken]);

  const handleFilterChange = (next: AuditLogFilters): void => {
    setFilters(next);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  const hasRows = useMemo(() => rows.length > 0, [rows]);

  return (
    <div>
      <PageHeader title="Audit logs" subtitle="Search and review administrative activity across the platform." />

      <AuditFiltersPanel filters={filters} companies={companies} onChange={handleFilterChange} />

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading audit logs…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && !hasRows ? (
        <EmptyState
          title="No audit log entries"
          description="No admin activity matches the current filters."
        />
      ) : null}

      {!loading && !error && hasRows ? (
        <>
          <AuditLogTable rows={rows} onView={setSelected} />
          <div className="bo-admin-pagination">
            <span className="bo-muted">
              Showing {rangeStart}–{rangeEnd} of {totalCount}
            </span>
            <div className="bo-admin-pagination-controls">
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="bo-muted">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : null}

      <AuditLogDetailModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
